<template>
  <div class="personal-info-modal">
    <modal @close-modal="closeModal">
      <div class="pi-content">
        <h2>Personal Information</h2>
        <p class="pi-subtitle">Please provide some basic information to complete this deposit.</p>

        <div class="pi-field-row">
          <div class="pi-field-group pi-half">
            <label for="pi-first-name">First Name</label>
            <input
              id="pi-first-name"
              v-model="firstName"
              type="text"
              placeholder="First name"
            />
          </div>
          <div class="pi-field-group pi-half">
            <label for="pi-last-name">Last Name</label>
            <input
              id="pi-last-name"
              v-model="lastName"
              type="text"
              placeholder="Last name"
            />
          </div>
        </div>

        <div class="pi-field-row">
          <div class="pi-field-group pi-half">
            <label for="pi-dob">Date of Birth</label>
            <input
              id="pi-dob"
              v-model="dateOfBirth"
              type="date"
              :max="maxDobDate"
              :class="{ 'pi-input-error': isUnderage, 'pi-has-value': dateOfBirth }"
            />
            <p v-if="isUnderage" class="pi-field-error">
              You must be at least 18 years old.
            </p>
          </div>
          <div class="pi-field-group pi-half">
            <label for="pi-country">Country of Residence</label>
            <input
              id="pi-country"
              v-model="countryOfResidence"
              type="text"
              list="pi-country-list"
              placeholder="Start typing..."
              autocomplete="off"
              :class="{ 'pi-input-error': isUS }"
            />
            <datalist id="pi-country-list">
              <option v-for="c in countries" :key="c" :value="c" />
            </datalist>
            <p v-if="isUS" class="pi-field-error">
              US residents are not eligible. Please withdraw your funds.
            </p>
          </div>
        </div>

        <button
          class="pi-submit-btn"
          :disabled="formIncomplete || submitting"
          @click="submit"
        >
          {{ submitting ? 'Saving...' : 'Submit' }}
        </button>

        <p v-if="errorMessage" class="pi-error-message">{{ errorMessage }}</p>
      </div>
    </modal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Modal from '@/components/global/Modal.vue';
import { useAPI } from '@/composables/useAPI';
import { useUserStore } from '@/stores/user';
import { emitter } from '@/core/emitter.js';

const emit = defineEmits(['close', 'completed']);

const userStore = useUserStore();

const firstName = ref('');
const lastName = ref('');
const dateOfBirth = ref('');
const countryOfResidence = ref('');
const submitting = ref(false);
const errorMessage = ref('');

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola',
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile',
  'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon',
  'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece',
  'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India',
  'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
  'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia',
  'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago',
  'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
  'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

const maxDobDate = computed(() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split('T')[0];
});

const isUnderage = computed(() => {
  if (!dateOfBirth.value) return false;
  const dob = new Date(dateOfBirth.value);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age < 18;
});

const isUS = computed(() => countryOfResidence.value === 'United States');

const formIncomplete = computed(() => {
  return !firstName.value || !lastName.value || !dateOfBirth.value
    || !countries.includes(countryOfResidence.value) || isUnderage.value || isUS.value;
});

function closeModal() {
  emit('close');
}

async function submit() {
  submitting.value = true;
  errorMessage.value = '';

  try {
    const { request } = useAPI('user/updatePersonalInfo');
    await request({
      firstName: firstName.value,
      lastName: lastName.value,
      dateOfBirth: dateOfBirth.value,
      countryOfResidence: countryOfResidence.value,
    });

    userStore.user.isAlphaUser = false;
    userStore.user.firstName = firstName.value;
    userStore.user.lastName = lastName.value;
    userStore.user.dateOfBirth = dateOfBirth.value;
    userStore.user.countryOfResidence = countryOfResidence.value;

    emitter.emit('showAlert', {
      type: 'personal-info',
      status: 'ok',
      title: 'Information Saved',
      detail: 'Your personal information has been saved successfully.',
    });

    emit('completed');
  } catch (err) {
    errorMessage.value = err?.message || 'Something went wrong. Please try again.';
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
.pi-content {
  padding: 0.5rem 0;
}

.pi-content h2 {
  font-size: 1.35rem;
  font-weight: 600;
  color: var(--color-primary_01);
  margin: 0 0 0.25rem;
}

.pi-subtitle {
  font-size: 14px;
  color: var(--grey-2200);
  margin: 0 0 1.5rem;
}

.pi-field-row {
  display: flex;
  gap: 0.75rem;
}

@media screen and (max-width: 576px) {
  .pi-field-row {
    flex-direction: column;
    gap: 0;
  }
}

.pi-field-group {
  margin-bottom: 1rem;
}

.pi-field-group label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: var(--grey-2200);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pi-field-group input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  border: 1px solid var(--grey-1200-1);
  border-radius: 8px;
  background: var(--white_01);
  color: var(--color-primary_01);
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.pi-field-group input::placeholder {
  color: var(--grey-2200);
}

.pi-field-group input:focus {
  border-color: var(--blue-100);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.pi-field-group input[type="date"] {
  color: var(--grey-2200);
}

.pi-field-group input[type="date"].pi-has-value {
  color: var(--color-primary_01);
}

.pi-half {
  flex: 1;
  min-width: 0;
}

.pi-input-error {
  border-color: #ea3b44 !important;
}

.pi-input-error:focus {
  box-shadow: 0 0 0 2px rgba(234, 59, 68, 0.15) !important;
}

.pi-field-error {
  font-size: 12px;
  color: #ea3b44;
  margin: 4px 0 0;
}

.pi-submit-btn {
  width: 100%;
  padding: 12px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  color: #fff;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.2s;
  margin-top: 0.5rem;
}

.pi-submit-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.pi-submit-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pi-error-message {
  font-size: 13px;
  color: #ea3b44;
  text-align: center;
  margin-top: 0.75rem;
}
</style>
